import {
  createAsyncThunk,
  createSlice,
  createEntityAdapter,
} from "@reduxjs/toolkit";
import { enqueueSnackbar } from "notistack";

import request from "../../utils/request";
import requestAI from "../../utils/request-ai";

import { RequestPageOutlined } from "@mui/icons-material";

// Entity adapter for assessments
const assessmentsAdapter = createEntityAdapter({
  selectId: (assessment) => assessment.id,
});

const initialState = assessmentsAdapter.getInitialState({
  isLoading: false,
  page: 0,
  searchPage: 0,
  error: null,
  filter: {},
  recentlyAddedAssessments: [],
  recentlyAddedAssessmentsCount: 0,
});

// Async thunk for fetching create assessment
export const createOrEditAsessment = createAsyncThunk(
  "assessments/createOrEditAsessment",
  async ({ data, cb }, { getState, rejectWithValue }) => {
    try {
      const { auth } = getState();
      const company_id = auth.user?.company_id;

      const response = await request({
        url: "/assessment/create",
        method: "POST",
        data: { ...data, company_id },
      });

      if (typeof cb === "function") cb(response?.data);

      console.log("CREATE OR EDIT ASSESSMENT", response);

      return response?.data;
    } catch (error) {
      enqueueSnackbar(error?.message, {
        autoHideDuration: 3000,
        variant: "error",
      });
      return rejectWithValue(error?.message);
    }
  }
);

// Async thunk for fetching all assessments
export const getAllAssessments = createAsyncThunk(
  "assessments/getAll",
  async (data, { getState, rejectWithValue }) => {
    try {
      const { auth } = getState();
      const company_id = auth.user?.company_id;

      const response = await request({
        url: "/assessment/get-all",
        method: "POST",
        data: {
          departmentIds: data?.departmentIds,
          skip: data?.page,
          take: 10,
          filter: data?.filter,
          company_id,
        },
      });

      return {
        filter: data?.filter,
        page: data?.page,
        data: response.data,
      };
    } catch (error) {
      enqueueSnackbar(error?.message, {
        autoHideDuration: 3000,
        variant: "error",
      });
      return rejectWithValue(error?.message);
    }
  }
);

// Async thunk for fetching all assessments
export const getRecentlyAddedAssessments = createAsyncThunk(
  "assessments/getRecentlyAddedAssessments",
  async ({ page = 1, take = 4 }, { getState, rejectWithValue }) => {
    try {
      const { auth } = getState();
      const company_id = auth.user?.company_id;

      const response = await request({
        url: "/assessment/get-recently-created",
        method: "POST",
        data: {
          take,
          skip: page,
          company_id,
        },
      });

      return { data: response?.data?.data, count: response?.data?.count };
    } catch (error) {
      enqueueSnackbar(error?.message, {
        autoHideDuration: 3000,
        variant: "error",
      });
      return rejectWithValue(error?.message);
    }
  }
);

// Async thunk for fetching all assessments
export const getFilterData = createAsyncThunk(
  "assessments/getFilterData",
  async (_, { getState, rejectWithValue }) => {
    try {
      const { auth } = getState();
      const company_id = auth.user?.company_id;

      const response = await request({
        url: `/assessment/get-filter-data?company_id=${company_id}`,
        method: "GET",
      });

      return {
        data: response.data,
      };
    } catch (error) {
      enqueueSnackbar(error?.message, {
        autoHideDuration: 3000,
        variant: "error",
      });
      return rejectWithValue(error?.message);
    }
  }
);

// Async thunk for searching assessments
export const applyAndSearchFilter = createAsyncThunk(
  "assessments/applyAndSearchFilter",
  async (filters, { rejectWithValue }) => {
    try {
      const page = 1;

      console.log("filters", filters);

      const response = await request({
        url: "/assessment/get-filtered-assessments",
        method: "POST",
        data: { ...filters, skip: page, take: 10 },
      });

      return {
        page,
        data: response.data,
      };
    } catch (error) {
      enqueueSnackbar(error?.message, {
        autoHideDuration: 3000,
        variant: "error",
      });
      return rejectWithValue(error?.message);
    }
  }
);

// Async thunk for searching assessments
export const searchAssessments = createAsyncThunk(
  "assessments/search",
  async (filters, { rejectWithValue }) => {
    try {
      const page = 1;

      const response = await request({
        url: "/assessments/search",
        method: "POST",
        data: { ...filters },
        params: { skip: page, limit: 100 },
      });

      return {
        page,
        data: response.data,
      };
    } catch (error) {
      enqueueSnackbar(error?.message, {
        autoHideDuration: 3000,
        variant: "error",
      });
      return rejectWithValue(error?.message);
    }
  }
);

// Async thunk for getting a single department assessment
export const getSingleDepartmentAssessment = createAsyncThunk(
  "departments/assessments",
  async (id, { rejectWithValue }) => {
    try {
      const response = await request({
        url: `/departments/assessments?departmentId=${id}`,
        method: "GET",
      });

      return response.data;
    } catch (error) {
      enqueueSnackbar(error?.message, {
        autoHideDuration: 3000,
        variant: "error",
      });
      return rejectWithValue(error?.message);
    }
  }
);

// Async thunk for getting a single assessment
export const getSingleAssessment = createAsyncThunk(
  "assessments/getSingle",
  async (id, { rejectWithValue }) => {
    try {
      const response = await request({
        url: `/assessments/get-single/${id}`,
        method: "GET",
      });

      return response.data;
    } catch (error) {
      enqueueSnackbar(error?.message, {
        autoHideDuration: 3000,
        variant: "error",
      });
      return rejectWithValue(error?.message);
    }
  }
);

// Async thunk for sending invite to candidates for interview
export const inviteCandidatesForInterview = createAsyncThunk(
  "assessments/inviteCandidatesForInterview",
  async (payload, { getState, rejectWithValue }) => {
    try {
      const state = getState();

      const response = await request({
        url: "/interview/create-by-assessment",
        method: "POST",
        data: payload,
      });

      enqueueSnackbar(response?.message || "Invite sent", {
        variant: "success",
      });

      return response.data;
    } catch (error) {
      enqueueSnackbar(error?.message, {
        autoHideDuration: 3000,
        variant: "error",
      });
      return rejectWithValue(error?.message);
    }
  }
);

// Async thunk for getCompanyAssessmentForm
export const getCompanyAssessmentForm = createAsyncThunk(
  "assessments/getCompanyAssessmentForm",
  async (payload, { getState, rejectWithValue }) => {
    try {
      const state = getState();

      const response = await request({
        url: "/interview/get-company-assessment-forms",
        method: "POST",
        data: payload,
      });

      return response.data;
    } catch (error) {
      enqueueSnackbar(error?.message, {
        autoHideDuration: 3000,
        variant: "error",
      });
      return rejectWithValue(error?.message);
    }
  }
);

// Async thunk for updateAssessmentForm
export const updateAssessmentForm = createAsyncThunk(
  "assessments/updateAssessmentForm",
  async (payload, { getState, rejectWithValue }) => {
    try {
      const state = getState();

      const response = await request({
        url: "/interview/update-company-assessment-form",
        method: "PUT",
        data: payload,
      });

      console.log("FORM Updated", response?.data);

      return response.data;
    } catch (error) {
      enqueueSnackbar(error?.message, {
        autoHideDuration: 3000,
        variant: "error",
      });
      return rejectWithValue(error?.message);
    }
  }
);

// Async thunk for createAssessmentForm
export const createAssessmentForm = createAsyncThunk(
  "assessments/createAssessmentForm",
  async (payload, { getState, rejectWithValue }) => {
    try {
      const state = getState();

      const response = await request({
        url: "/interview/create-assessment-form",
        method: "POST",
        data: payload,
      });

      console.log("FORM CREATED", response?.data);

      return response.data;
    } catch (error) {
      enqueueSnackbar(error?.message, {
        autoHideDuration: 3000,
        variant: "error",
      });
      return rejectWithValue(error?.message);
    }
  }
);

// Async thunk for deleteAssessmentForm
export const deleteAssessmentForm = createAsyncThunk(
  "assessments/deleteAssessmentForm",
  async (id, { getState, rejectWithValue }) => {
    try {
      const state = getState();

      const response = await request({
        url: `/interview/delete-assessment-form?id=${id}`,
        method: "DELETE",
      });

      console.log("FORM DELETED", response?.data);

      return response.data;
    } catch (error) {
      enqueueSnackbar(error?.message, {
        autoHideDuration: 3000,
        variant: "error",
      });
      return rejectWithValue(error?.message);
    }
  }
);

// Async thunk for duplicateAssessmentForm
export const duplicateAssessmentForm = createAsyncThunk(
  "assessments/duplicateAssessmentForm",
  async (id, { getState, rejectWithValue }) => {
    try {
      const state = getState();

      const response = await request({
        url: `/interview/duplicate-assessment-form?id=${id}`,
        method: "POST",
      });

      console.log("DUPLICATE FORM CREATED", response?.data);

      return response.data;
    } catch (error) {
      enqueueSnackbar(error?.message, {
        autoHideDuration: 3000,
        variant: "error",
      });
      return rejectWithValue(error?.message);
    }
  }
);

// Async thunk for sending invite to candidates for interview
export const inviteCandidatesForInterviewViaLink = createAsyncThunk(
  "assessments/inviteCandidatesForInterviewViaLink",
  async (payload, { getState, rejectWithValue }) => {
    try {
      const state = getState();

      const response = await request({
        url: "/interview/create-by-assessment-link",
        method: "POST",
        data: payload,
      });

      enqueueSnackbar(response?.message || "Invite sent", {
        variant: "success",
      });

      return response;
    } catch (error) {
      enqueueSnackbar(error?.message, {
        autoHideDuration: 3000,
        variant: "error",
      });
      return rejectWithValue(error?.message);
    }
  }
);

export const createAssessmentByAi = createAsyncThunk(
  "assessments/createAssessmentByAi",
  async (data, { getState, rejectWithValue }) => {
    try {
      const { auth } = getState();
      const user_id = auth.user?.id;

      const response = await requestAI({
        url: `/create_assessment_with_ai`,
        method: "POST",
        data,
      });

      console.log("Response from create assessment by ai", response);

      return response;
    } catch (error) {
      enqueueSnackbar(error?.message, {
        autoHideDuration: 3000,
        variant: "error",
      });
      return rejectWithValue(error?.message);
    }
  }
);

// Slice
const assessmentsSlice = createSlice({
  name: "assessments",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(createOrEditAsessment.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(createOrEditAsessment.fulfilled, (state, action) => {
        state.isLoading = false;
        state.searchPage = 0;
        assessmentsAdapter.updateOne(state, action.payload);
      })
      .addCase(createOrEditAsessment.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload || action.error.message;
      })
      .addCase(getAllAssessments.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getAllAssessments.fulfilled, (state, action) => {
        state.isLoading = false;

        const { page, data, filter } = action.payload;
        console.log("filter", filter);
        const isFilterApplied = Object.values(filter || {}).flat().length;

        if (data) {
          if (page === 1 && isFilterApplied) {
            assessmentsAdapter.setAll(state, data || []);
          } else if (data) {
            assessmentsAdapter.upsertMany(state, data || []);
          }
        }
        state.page = page;
      })
      .addCase(getAllAssessments.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload || action.error.message;
      })
      .addCase(getRecentlyAddedAssessments.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getRecentlyAddedAssessments.fulfilled, (state, action) => {
        state.isLoading = false;

        const { count, data } = action.payload;
        assessmentsAdapter.upsertMany(state, data || []);

        const ids = data?.map((a) => a?.id);
        state.recentlyAddedAssessments = ids;
        state.recentlyAddedAssessmentsCount = count;
      })
      .addCase(getRecentlyAddedAssessments.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload || action.error.message;
      })

      .addCase(getFilterData.pending, (state) => {
        state.error = null;
      })
      .addCase(getFilterData.fulfilled, (state, action) => {
        state.isLoading = false;
        state.filter = action.payload.data || {};
      })
      .addCase(getFilterData.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload || action.error.message;
      })

      .addCase(applyAndSearchFilter.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(applyAndSearchFilter.fulfilled, (state, action) => {
        state.isLoading = false;

        const { page, data } = action.payload;

        if (data && data?.length) {
          if (page === 1) {
            assessmentsAdapter.setAll(state, data || []);
          } else if (data?.length > 0) {
            assessmentsAdapter.upsertMany(state, data || []);
          }
        }
        state.page = page;
      })
      .addCase(applyAndSearchFilter.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload || action.error.message;
      })

      .addCase(getSingleDepartmentAssessment.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getSingleDepartmentAssessment.fulfilled, (state, action) => {
        state.isLoading = false;
        state.searchPage = 0;
        assessmentsAdapter.setAll(state, action.payload.assessments || []);
      })
      .addCase(getSingleDepartmentAssessment.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload || action.error.message;
      })

      .addCase(searchAssessments.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(searchAssessments.fulfilled, (state, action) => {
        state.isLoading = false;
        state.page = 0;
        assessmentsAdapter.setAll(state, action.payload.data || []);
      })
      .addCase(searchAssessments.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload || action.error.message;
      })

      .addCase(getSingleAssessment.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getSingleAssessment.fulfilled, (state, action) => {
        state.isLoading = false;
        assessmentsAdapter.upsertOne(state, action.payload);
      })
      .addCase(getSingleAssessment.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload || action.error.message;
      });
  },
});

// Export selectors using the adapter
export const {
  selectAll: selectAllAssessments,
  selectById: selectAssessmentById,
  selectIds: selectAssessmentIds,
} = assessmentsAdapter.getSelectors((state) => state.assessments);

export const selectLoading = (state) => state.assessments.isLoading;
export const selectPage = (state) => state.assessments.page;
export const selectError = (state) => state.assessments.error;

export default assessmentsSlice.reducer;
